import styled from "@emotion/styled";
import { Box, Button, FlexRow, H2, IconArrow, Stack } from "atoms";
import { ProductWizardTemplate } from "./ProductWizardTemplate";
import { ProductWizardProgressBar } from "./ProductWizardProgressBar";
import type { CreateProductState } from "../types";

type ProductWizardStepProps = {
  /** Product wizard state for ProgressBar */
  state: CreateProductState;
  /** Step title appearing above the ProgressBar */
  stepTitle: string;
  /** Optional cancel button onClick handler */
  moveBack?: VoidFunction;
  /** Disable the cancel/back button */
  moveBackDisabled?: boolean;
  /** Disable the next/submit button */
  nextStepDisabled?: boolean;
  children: React.ReactNode;
} & (
  | {
      /** Optionally render step as a form element with next button set to type="submit" */
      isForm: true;
      /** Optional next button onClick handler or form onSubmit handler */
      nextStep: React.FormEventHandler<HTMLFormElement>;
    }
  | {
      /** Optionally render step as a form element with next button set to type="submit" */
      isForm?: false;
      /** Optional next button onClick handler or form onSubmit handler */
      nextStep?: VoidFunction;
    }
);

function ProductWizardStep({
  state,
  stepTitle,
  moveBack,
  moveBackDisabled = false,
  nextStepDisabled = false,
  children,
  ...rest
}: ProductWizardStepProps) {
  // Determine if we're working with a Form Event or Click Event handler
  let buttonProps: Partial<React.ComponentProps<typeof Button>>;
  let wrapperProps: Partial<React.ComponentProps<typeof Wrapper>>;
  if (rest.isForm) {
    buttonProps = { type: "submit" };
    wrapperProps = { onSubmit: rest.nextStep };
  } else {
    buttonProps = { onClick: rest.nextStep };
    wrapperProps = { as: "div" };
  }

  return (
    <ProductWizardTemplate title="Product Builder">
      <Wrapper {...wrapperProps}>
        <Stack gap="sm">
          <H2 size="md" textTransform="none" textAlign="center">
            {stepTitle}
          </H2>

          <ProductWizardProgressBar state={state} />
        </Stack>

        <Box fullWidth>
          <div>{children}</div>
        </Box>

        <WizardFooter>
          <FlexRow justifyContent="space-between">
            {!!state.completedSteps.length && moveBack ? (
              <Button
                size="xs"
                variant="secondary"
                onClick={moveBack}
                disabled={moveBackDisabled}
              >
                <IconArrow dir="l" /> Back
              </Button>
            ) : (
              <span />
            )}

            {(rest.isForm || !!rest.nextStep) && (
              <Button size="xs" disabled={nextStepDisabled} {...buttonProps}>
                Save and Continue <IconArrow dir="r" />
              </Button>
            )}
          </FlexRow>
        </WizardFooter>
      </Wrapper>
    </ProductWizardTemplate>
  );
}

const Wrapper = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
});

const WizardFooter = styled.div(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  paddingBottom: "calc(env(safe-area-inset-bottom, 1rem) + 10px)",
  paddingTop: 10,
  zIndex: 10,
  "&>*": { position: "relative", zIndex: 2 },
  "&::before, &::after": {
    content: "''",
    position: "absolute",
    top: 0,
    bottom: -1,
    left: "50%",
    width: "100dvw",
    transform: "translateX(-50%)",
    backgroundColor: theme.colors.bg,
    zIndex: 1,
  },
  "&::before": {
    boxShadow: theme.boxShadow.light,
    bottom: "auto",
    height: 10,
  },
}));

export { ProductWizardStep };
